class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (e) => {
      if (e.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.querySelector('cart-drawer-banner')?.play?.();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.querySelector('cart-drawer-banner')?.pause?.();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, forceUpdate = false) {
    const cartBehavior = this.dataset.cartBehavior || "drawer";
    const cartType = this.dataset.cartType || "drawer";

    this.querySelector(".drawer__inner")?.classList.remove("is-empty");
    this.productId = parsedState.id;

    const sectionsMap = parsedState && parsedState.sections ? parsedState.sections : null;
    if (!sectionsMap) return;

    const parsedSections = {};
    const parseSection = (id) =>
      (parsedSections[id] ??= new DOMParser().parseFromString(sectionsMap[id], 'text/html'));

    if (sectionsMap['cart-drawer']) {
      const renderedDrawer = parseSection('cart-drawer').querySelector('cart-drawer');
      if (renderedDrawer) this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !sectionsMap[section.id]) return;

      const newHtml = parseSection(section.id).querySelector(section.selector || '.shopify-section')?.innerHTML;
      if (newHtml) sectionElement.innerHTML = newHtml;
    });

    // ✅ Skip drawer open animation unless forced
    if ((cartType !== "drawer" || cartBehavior !== "drawer") && !forceUpdate)
      return;

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerBanner extends HTMLElement {
  connectedCallback() {
    this.mountSlider();
  }

  disconnectedCallback() {
    this.slider?.destroy(true);
    this.slider = null;
  }

  mountSlider() {
    const root = this.querySelector('.splide');
    if (!root || this.slider) return;

    if (!window.Splide) {
      window.addEventListener('load', () => this.isConnected && this.mountSlider(), { once: true });
      return;
    }

    this.slider = new Splide(root, {
      type: 'loop',
      perPage: 1,
      arrows: false,
      pagination: true,
      autoplay: this.dataset.autoplay === 'true',
      interval: Number(this.dataset.interval) || 4000,
      pauseOnHover: true,
      pauseOnFocus: true,
    }).mount();

    if (!this.closest('cart-drawer')?.classList.contains('active')) this.pause();
  }

  play() {
    if (this.dataset.autoplay === 'true') this.slider?.Components.Autoplay.play();
  }

  pause() {
    this.slider?.Components.Autoplay.pause();
  }
}

customElements.define('cart-drawer-banner', CartDrawerBanner);

class CartUpsellSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.cart-upsell-row__list');
    this.prevButton = this.querySelector('.splide__arrow--prev');
    this.nextButton = this.querySelector('.splide__arrow--next');
    if (!this.list || !this.prevButton || !this.nextButton) return;

    this.updateArrows = this.updateArrows.bind(this);
    this.prevButton.addEventListener('click', () => this.scrollByItem(-1));
    this.nextButton.addEventListener('click', () => this.scrollByItem(1));
    this.list.addEventListener('scroll', this.updateArrows, { passive: true });
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.resizeObserver.observe(this.list);
    this.updateArrows();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  scrollByItem(direction) {
    const item = this.list.querySelector('.cart-upsell-row__item');
    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.list.clientWidth;
    this.list.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  updateArrows() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth - 1;
    this.prevButton.disabled = this.list.scrollLeft <= 0;
    this.nextButton.disabled = this.list.scrollLeft >= maxScroll;
  }
}

customElements.define('cart-upsell-slider', CartUpsellSlider);
